import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { CamptainLoader } from './CamptainLoader';

interface LoadingProps {
  label?: string;
  /** Set a height for a local panel loader; omitted loaders fill the main page content. */
  minHeight?: number | string;
  /** Ring diameter in px. Defaults smaller for inline/tab-panel loads. */
  size?: number;
}

/** Page loaders fill the content background; dialogs and explicitly sized panels stay local. */
export function Loading({ label = 'Loading…', minHeight, size = 84 }: LoadingProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pageRegion, setPageRegion] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || minHeight !== undefined || anchor.closest('[role="dialog"], .MuiDrawer-paper')) {
      setPageRegion(null);
      return;
    }
    setPageRegion(anchor.closest<HTMLElement>('[data-page-loading-region]'));
  }, [minHeight]);

  const loader = (
    <Box
      aria-busy="true"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        p: 3,
        boxSizing: 'border-box',
        ...(pageRegion
          ? { position: 'absolute', inset: 0, zIndex: 30, bgcolor: 'background.default' }
          : { minHeight: minHeight ?? 240 }),
      }}
    >
      <CamptainLoader size={size} label={label} />
    </Box>
  );

  return (
    <>
      <span ref={anchorRef} hidden />
      {pageRegion ? createPortal(loader, pageRegion) : loader}
    </>
  );
}
/** Rectangular skeleton rows for table/list placeholders. */
export function SkeletonRows({ rows = 6, height = 44 }: { rows?: number; height?: number }) {
  return (
    <Stack spacing={1} sx={{ width: '100%', p: 1 }} aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={height} />
      ))}
    </Stack>
  );
}
